import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { QUICK_ADD } from "@/config/nav";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Search, Plus, Bell, HelpCircle } from "lucide-react";
import { OrgSwitcher } from "@/components/shell/OrgSwitcher";

export function TopBar({ onOpenPalette, onOpenNotifications, onQuickAdd }) {
  const { db, can } = useApp();
  const navigate = useNavigate();
  const unread = db.notifications.filter((n) => !n.read).length;
  const quickItems = QUICK_ADD.filter((q) => can(q.perm));

  return (
    <header className="flex h-[68px] shrink-0 items-center gap-4 border-b border-line bg-surface px-5">
      <OrgSwitcher />
      <button
        onClick={onOpenPalette}
        data-testid="global-search-btn"
        className="flex h-10 max-w-xl flex-1 items-center gap-2.5 rounded-lg border border-line bg-card px-3.5 text-left text-sm text-ink-muted transition-colors hover:border-copper/40"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 truncate">Search matters, clients, documents, citations…</span>
        <kbd className="hidden rounded border border-line bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-ink-2 sm:inline">⌘K</kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        {quickItems.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-1.5" data-testid="quick-add-btn">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Quick Add</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {quickItems.map((q) => (
                <DropdownMenuItem key={q.key} onClick={() => onQuickAdd(q.key)} data-testid={`quick-add-${q.key}`}>
                  {q.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onOpenNotifications}
                data-testid="notifications-btn"
                className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-card text-ink-2 hover:border-copper/40"
              >
                <Bell className="h-[18px] w-[18px]" />
                {unread > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-copper px-1 text-[10px] font-semibold text-white">
                    {unread}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>Notifications</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate("/settings")}
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-line bg-card text-ink-2 hover:border-copper/40"
              >
                <HelpCircle className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Help</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </header>
  );
}
